import { useId } from 'react';
import { formatTime, sentenceCase } from '../lib/format.js';
import { isWaxing, moonLitPath } from '../lib/moon.js';
import { useEtMinutes } from '../lib/use-et-minutes.js';

// The nameplate's two ears on the live front page, set either side of it the
// way a newspaper sets its weather boxes: a tide clock on the left and
// tonight's moon on the right, each drawn from the brief's own numbers with
// two lines under it.
//
// Added 2026-10-02. James, with the harbor chart new in the margins:
// "Beautiful, but how do we enhance this top header". The masthead was the
// one plain thing left on the first screen, with empty paper either side of
// the wordmark. The two instruments are the almanac's own, they share the
// chart's engraved line, and like the chart they keep the real day: the
// clock's hand reaches the top at the brief's high water to the minute, and
// "Tide rising" is what the water-lines in the margins are doing.
//
// Only at 1120px and wider, where the chart sits in the margins and the paper
// beside the wordmark is clear. Narrower, the chart is a band behind the
// masthead and that paper is its to draw on.

// Palette tokens (tailwind.config.js), as SVG attributes like DayStrip's.
const INK = '#16263c'; // navy-900
const NAVY_700 = '#274263';
const LABEL = '#30517a'; // navy-600
const GOLD = '#cf9844'; // cream-500
const PAPER = '#fdfaf3'; // cream-50
const MOON_LIT = '#e6cd92'; // cream-300, the day strip's moon
const FONT = 'Inter, system-ui, -apple-system, sans-serif';

const S = 72; // the dials' size, px
const R = S / 2 - 0.75; // the bezel, inside the stroke
const FACE = R - 11; // the tide clock's face, inside its lettered rim

function toMin(hhmm) {
  const m = typeof hhmm === 'string' ? hhmm.match(/^(\d{1,2}):(\d{2})/) : null;
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

// Where the harbor is in its tide at `now` (minutes past midnight ET): the
// high or low it last passed, the one it is heading for, and how far round
// the clock the hand sits, as a fraction clockwise from high water at the top.
// A tide clock goes down the right side from high water to low and up the
// left from low back to high. Before the day's first listed tide or after its
// last, the missing turn is put one half-tide away (the gap between the two
// nearest listed turns); it places the hand, and the words only ever name a
// listed time.
export function tideState(events, now) {
  const turns = (Array.isArray(events) ? events : [])
    .map((e) => ({ type: e?.type === 'high' || e?.type === 'low' ? e.type : null, min: toMin(e?.time), time: e?.time }))
    .filter((e) => e.type && e.min != null)
    .sort((a, b) => a.min - b.min);
  if (turns.length < 2 || now == null) return null;
  const at = turns.findIndex((e) => e.min > now);
  const other = (type) => (type === 'high' ? 'low' : 'high');
  let prev;
  let next;
  if (at === -1) {
    prev = turns[turns.length - 1];
    next = { type: other(prev.type), min: 2 * prev.min - turns[turns.length - 2].min, guessed: true };
  } else if (at === 0) {
    next = turns[0];
    prev = { type: other(next.type), min: 2 * next.min - turns[1].min, guessed: true };
  } else {
    prev = turns[at - 1];
    next = turns[at];
  }
  // Highs and lows alternate; a list that does not is not one to draw from.
  if (prev.type === next.type || next.min <= prev.min) return null;
  const f = Math.max(0, Math.min(1, (now - prev.min) / (next.min - prev.min)));
  return { prev, next, rising: next.type === 'high', turn: prev.type === 'high' ? f / 2 : 0.5 + f / 2 };
}

const P = (n) => n.toFixed(2);

// A word set round the rim, over the top or under the bottom, reading left to
// right either way. The bottom path sits further out, since its letters stand
// inward from it.
function RimText({ id, text, r, top }) {
  const d = top ? `M ${P(-r)} 0 A ${r} ${r} 0 0 1 ${P(r)} 0` : `M ${P(-r)} 0 A ${r} ${r} 0 0 0 ${P(r)} 0`;
  return (
    <>
      <path id={id} d={d} fill="none" />
      <text fontFamily={FONT} fontWeight={500} fontSize={5.6} letterSpacing={1.1} fill={LABEL}>
        <textPath href={`#${id}`} startOffset="50%" textAnchor="middle">
          {text}
        </textPath>
      </text>
    </>
  );
}

function Bezel() {
  return (
    <>
      <circle r={R} fill={PAPER} stroke={INK} strokeWidth={1} />
      <circle r={R - 2.6} fill="none" stroke={INK} strokeWidth={0.45} />
    </>
  );
}

function TideClock({ turn }) {
  // useId's colons are not safe inside url() and href fragments everywhere.
  const id = useId().replace(/:/g, '');
  const L = FACE - 3.5; // the hand's reach
  return (
    <svg width={S} height={S} viewBox={`${-S / 2} ${-S / 2} ${S} ${S}`} aria-hidden="true" className="block">
      <Bezel />
      <circle r={FACE} fill="none" stroke={INK} strokeWidth={0.45} />
      <RimText id={`${id}-hw`} text="HIGH WATER" r={R - 8.6} top />
      <RimText id={`${id}-lw`} text="LOW WATER" r={R - 4.4} />
      {/* Twelve ticks, six a side: a half-tide is a little over six hours,
          so each is about an hour. */}
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * 2 * Math.PI;
        const long = i % 3 === 0;
        const r0 = FACE - (long ? 4 : 2.5);
        return (
          <line
            key={i}
            x1={P(Math.sin(a) * r0)}
            y1={P(-Math.cos(a) * r0)}
            x2={P(Math.sin(a) * FACE)}
            y2={P(-Math.cos(a) * FACE)}
            stroke={INK}
            strokeWidth={long ? 0.9 : 0.55}
          />
        );
      })}
      {/* The hand, a slender spade with a gold head. The angle is the SVG
          transform; the settle on first paint (index.css) animates the CSS
          rotate on top of it, so a browser without that property still sets
          the hand right. */}
      <g transform={`rotate(${P(turn * 360)})`}>
        <g className="tide-hand">
          <line x1={0} y1={6} x2={0} y2={P(-L + 7)} stroke={INK} strokeWidth={1.1} strokeLinecap="round" />
          <path
            d={`M 0 ${P(-L)} C 2.6 ${P(-L + 3)} 2.8 ${P(-L + 6)} 0 ${P(-L + 8.5)} C -2.8 ${P(-L + 6)} -2.6 ${P(-L + 3)} 0 ${P(-L)} Z`}
            fill={GOLD}
            stroke={INK}
            strokeWidth={0.6}
          />
          <circle cy={6} r={1.4} fill={INK} />
        </g>
      </g>
      <circle r={2.1} fill={INK} />
      <circle r={0.7} fill={PAPER} />
    </svg>
  );
}

function MoonDial({ lit, waxing }) {
  const id = useId().replace(/:/g, '');
  const m = R - 8.5;
  // The dark of the moon is engraved: hatching at 45 degrees, clipped to the
  // disc, with the lit part laid over it.
  const hatch = [];
  for (let k = -2 * m; k <= 2 * m; k += 2) hatch.push(k);
  return (
    <svg width={S} height={S} viewBox={`${-S / 2} ${-S / 2} ${S} ${S}`} aria-hidden="true" className="block">
      <Bezel />
      <clipPath id={id}>
        <circle r={m} />
      </clipPath>
      <g clipPath={`url(#${id})`}>
        {hatch.map((k) => (
          <line key={k} x1={P(k - m)} y1={P(-m)} x2={P(k + m)} y2={P(m)} stroke={NAVY_700} strokeWidth={0.5} opacity={0.75} />
        ))}
      </g>
      <path d={moonLitPath(0, 0, m, lit, waxing)} fill={MOON_LIT} />
      <circle r={m} fill="none" stroke={INK} strokeWidth={0.8} />
    </svg>
  );
}

function Ear({ side, children }) {
  return (
    <div
      className={[
        'hidden w-[124px] flex-col items-center text-center min-[1120px]:row-start-1 min-[1120px]:flex',
        side === 'left'
          ? 'min-[1120px]:col-start-1 min-[1120px]:justify-self-start'
          : 'min-[1120px]:col-start-3 min-[1120px]:justify-self-end'
      ].join(' ')}
    >
      {children}
    </div>
  );
}

export default function MastheadEars({ tides, sunrise, sunset, moon }) {
  const now = useEtMinutes(true);
  const tide = tideState(tides?.events, now);
  const lit = typeof moon?.illumination === 'number' ? Math.max(0, Math.min(1, moon.illumination / 100)) : null;
  const phase = typeof moon?.phase === 'string' && moon.phase ? sentenceCase(moon.phase) : null;
  // The sun's line: sunrise until it has come up, then the day's sunset.
  const rise = toMin(sunrise);
  const sunLine =
    rise != null && now != null && now < rise
      ? `Sunrise ${formatTime(sunrise)}`
      : toMin(sunset) != null
        ? `Sunset ${formatTime(sunset)}`
        : null;
  // Both or neither: one instrument alone would leave the masthead lopsided.
  if (!tide || lit == null || !phase || !sunLine) return null;

  const water = (type) => (type === 'high' ? 'High water' : 'Low water');
  // Within a quarter hour of a listed high or low the water is all but
  // still, so the clock says it is turning and names that turn. Otherwise it
  // names the turn the tide is heading for, unless that one is past the day's
  // last listed tide; then the last one, so no time here is ever a guess.
  const near = [tide.prev, tide.next].find((t) => !t.guessed && Math.abs(now - t.min) <= 15);
  const motion = near ? 'turning' : tide.rising ? 'rising' : 'falling';
  const tideLine = near
    ? `${water(near.type)} ${formatTime(near.time)}`
    : tide.next.guessed
      ? `${water(tide.prev.type)} was ${formatTime(tide.prev.time)}`
      : `${water(tide.next.type)} ${formatTime(tide.next.time)}`;

  return (
    <>
      <Ear side="left">
        <TideClock turn={tide.turn} />
        <p className="mt-2 font-serif text-sm leading-tight text-navy-900">Tide {motion}</p>
        <p className="mt-0.5 text-[11px] leading-snug text-navy-500">{tideLine}</p>
      </Ear>
      <Ear side="right">
        <MoonDial lit={lit} waxing={isWaxing(moon.phase)} />
        <p className="mt-2 font-serif text-sm leading-tight text-navy-900">
          {phase}
          <span className="sr-only"> moon</span>
        </p>
        <p className="mt-0.5 text-[11px] leading-snug text-navy-500">{sunLine}</p>
      </Ear>
    </>
  );
}
